/* SleepOn · Cabecera con menú (sections/sp-header.liquid).
   Hamburguesa → panel lateral con tarjetas de producto. Esc / overlay cierran, foco atrapado en el panel,
   contador del carrito del panel sincronizado con #cart-icon-bubble, y soporte del editor de temas.
   Autocontenido: no depende de otros JS (el cart drawer y el buscador son los del tema). */
if (!customElements.get('sp-header')) {
  class SpHeader extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.sph-panel');
      this.bar = this.querySelector('.sph-bar');
      this.section = this.closest('.shopify-section');
      this.toggles = Array.from(this.querySelectorAll('[data-sph-toggle]'));
      this.lastTrigger = null;

      this.toggles.forEach((btn) =>
        btn.addEventListener('click', () => {
          this.lastTrigger = btn;
          this.toggle();
        })
      );
      this.querySelectorAll('[data-sph-close]').forEach((el) => el.addEventListener('click', () => this.close(true)));

      this.onKeydown = (e) => {
        if (e.key === 'Escape' || e.key === 'Esc') {
          e.preventDefault();
          this.close(true);
          return;
        }
        if (e.key !== 'Tab' || !this.panel) return;
        const items = this.getFocusable();
        if (!items.length) {
          e.preventDefault();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const inside = this.panel.contains(document.activeElement);
        if (e.shiftKey && (document.activeElement === first || !inside)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
          e.preventDefault();
          first.focus();
        }
      };

      // Bolsa del panel → cierra el panel y abre el cart drawer del tema (que escucha el clic en #cart-icon-bubble)
      const panelCart = this.querySelector('[data-sph-cart]');
      if (panelCart) {
        panelCart.addEventListener('click', () => {
          this.close(false);
          const cartIcon = document.getElementById('cart-icon-bubble');
          if (cartIcon) {
            cartIcon.click();
          } else {
            const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
            window.location.href = root + 'cart';
          }
        });
      }

      this.syncCartCount();
      this.watchHeight();

      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = (e) => {
          if (this.contains(e.target)) this.open(false);
        };
        this.onBlockDeselect = (e) => {
          if (this.contains(e.target)) this.close(false);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    disconnectedCallback() {
      this.close(false);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.cartObserver) this.cartObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    isOpen() {
      return this.classList.contains('sph-open');
    }

    toggle() {
      this.isOpen() ? this.close(true) : this.open(true);
    }

    open(moveFocus = true) {
      if (this.isOpen() || !this.panel) return;
      // Cierra el buscador del tema si estaba abierto
      const search = this.querySelector('details-modal');
      if (search && typeof search.close === 'function' && search.querySelector('details[open]')) search.close(false);

      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbar > 0) document.body.style.paddingRight = scrollbar + 'px';
      document.body.style.overflow = 'hidden';

      this.panel.removeAttribute('inert');
      this.classList.add('sph-open');
      if (this.section) this.section.classList.add('sph-section--open');
      this.toggles.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      document.addEventListener('keydown', this.onKeydown);

      if (moveFocus) {
        requestAnimationFrame(() => {
          const target = this.getFocusable()[0] || this.panel;
          target.focus({ preventScroll: true });
        });
      }
    }

    close(returnFocus = false) {
      if (!this.isOpen()) return;
      this.classList.remove('sph-open');
      if (this.section) this.section.classList.remove('sph-section--open');
      this.toggles.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('keydown', this.onKeydown);
      if (this.panel) this.panel.setAttribute('inert', '');

      const trigger =
        (this.lastTrigger && this.lastTrigger.offsetParent !== null && this.lastTrigger) ||
        this.toggles.find((b) => b.offsetParent !== null);
      if (returnFocus && trigger) trigger.focus();
    }

    getFocusable() {
      return Array.from(
        this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.getClientRects().length > 0 && el.getAttribute('tabindex') !== '-1');
    }

    // El tema reescribe el contenido de #cart-icon-bubble tras cada cambio del carrito (sección "cart-icon-bubble").
    syncCartCount() {
      const mainCart = document.getElementById('cart-icon-bubble');
      const panelCount = this.querySelector('[data-sph-cart-count]');
      if (!mainCart || !panelCount) return;
      const sync = () => {
        const bubble = mainCart.querySelector('.cart-count-bubble span[aria-hidden]');
        const hasBubble = !!mainCart.querySelector('.cart-count-bubble');
        let count = bubble ? bubble.textContent.trim() : '';
        if (!count && hasBubble) count = '99+';
        panelCount.textContent = count || '0';
        panelCount.classList.toggle('sph-is-hidden', !count || count === '0');
      };
      sync();
      this.cartObserver = new MutationObserver(sync);
      this.cartObserver.observe(mainCart, { childList: true, subtree: true, characterData: true });
    }

    // Publica la altura de la barra (--header-height lo usan otras secciones del tema, como hacía sticky-header)
    watchHeight() {
      if (!this.bar) return;
      const set = () => {
        const h = Math.round(this.bar.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--header-height', h + 'px');
        document.documentElement.style.setProperty('--sph-header-h', h + 'px');
      };
      set();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(set);
        this.resizeObserver.observe(this.bar);
      }
    }
  }

  customElements.define('sp-header', SpHeader);
}
